import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, KeyRound, LogOut, Search } from "lucide-react";
import { toast } from "sonner";
import { http, STATUS_LABELS, formatApiError } from "../../lib/api";
import { Logo } from "../site/Logo";
import { OrdersTable } from "./OrdersTable";
import { RunSheet } from "./RunSheet";
import { Subscriptions } from "./Subscriptions";
import { PaymentSettings } from "./PaymentSettings";
import { ProductsAdmin } from "./ProductsAdmin";
import { ChangePassword } from "./ChangePassword";

const TITLES = { orders: "Orders", run: "Run sheet", subs: "Subscriptions", products: "Products", pay: "Payment details", account: "Change password" };

const TABS = ["all", "pending_payment", "paid", "packed", "delivered", "cancelled"];

const Stat = ({ label, value, id }) => (
  <div className="rounded-3xl border border-white/10 bg-[#161D17] p-6">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--sage)]">{label}</p>
    <p className="mt-2 font-display text-4xl font-extrabold" data-testid={`admin-stat-${id}`}>{value}</p>
  </div>
);

export const AdminDashboard = ({ user, onLogout }) => {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [view, setView] = useState("orders");

  useEffect(() => {
    http.get("/admin/orders")
      .then((r) => setOrders(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/admin/orders/${id}`, { status });
      setOrders((os) => os.map((o) => (o.id === id ? data : o)));
      const emailed = { paid: "marked paid", delivered: "marked delivered" }[status];
      toast.success(emailed ? `${data.ref} ${emailed} — customer emailed` : `${data.ref} → ${STATUS_LABELS[status]}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const setDelivery = async (id, date) => {
    try {
      const { data } = await http.patch(`/admin/orders/${id}/delivery`, { date });
      setOrders((os) => os.map((o) => (o.id === id ? data : o)));
      toast.success(`${data.ref} delivery set — customer emailed`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const shown = useMemo(() => {
    const s = q.toLowerCase();
    return orders.filter((o) => (tab === "all" || o.status === tab) &&
      [o.ref, o.name, o.suburb, o.email].some((v) => (v ?? "").toLowerCase().includes(s)));
  }, [orders, tab, q]);

  const live = orders.filter((o) => o.status !== "cancelled");
  const sum = (arr) => arr.reduce((a, o) => a + (o.total ?? 0), 0);

  return (
    <div className="mx-auto max-w-[1440px] px-5 py-8 sm:px-10" data-testid="admin-dashboard">
      <header className="no-print flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Logo /><span className="hidden font-display text-xl font-extrabold sm:inline">Go Box</span></Link>
        <div className="flex items-center gap-4">
          <span className="hidden text-sm text-[var(--sage)] lg:inline">{user?.email}</span>
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap rounded-full border border-white/15 px-4 py-2.5 text-sm font-semibold transition-colors hover:border-[var(--gold)] hover:text-[var(--gold)]" data-testid="admin-view-website-link"><ExternalLink className="h-4 w-4" /> View website</Link>
          <button onClick={() => setView("account")} className="qty-btn" aria-label="Change password" title="Change password" data-testid="admin-change-password-button"><KeyRound className="h-4 w-4" /></button>
          <button onClick={onLogout} className="qty-btn" aria-label="Log out" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <div className="no-print mt-12 flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-5xl font-extrabold tracking-tight">{TITLES[view]}</h1>
        <div className="grid w-full grid-cols-2 gap-1 rounded-3xl border border-white/10 p-1 sm:flex sm:w-auto">
          {[["orders", "Orders"], ["run", "Run sheet"], ["subs", "Subscriptions"], ["products", "Products"], ["pay", "Payment details"]].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${view === v ? "bg-white text-[#0F1410]" : "text-[var(--sage)] hover:text-white"}`} data-testid={`admin-view-${v}`}>{l}</button>
          ))}
        </div>
      </div>
      {view === "account" ? <ChangePassword onDone={() => setView("orders")} /> : view === "products" ? <ProductsAdmin /> : view === "pay" ? <PaymentSettings /> : view === "subs" ? <Subscriptions /> : view === "run" ? <RunSheet orders={orders} onStatus={setStatus} /> : <>
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Orders" value={live.length} id="orders" />
        <Stat label="Items" value={live.reduce((a, o) => a + (o.quantity ?? 0), 0)} id="boxes" />
        <Stat label="Awaiting payment" value={`$${sum(live.filter((o) => o.status === "pending_payment")).toFixed(0)}`} id="pending" />
        <Stat label="Paid revenue" value={`$${sum(live.filter((o) => o.status !== "pending_payment")).toFixed(0)}`} id="paid" />
      </div>
      <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`rounded-full border px-4 py-2 text-sm transition-colors ${tab === t ? "border-[var(--gold)] bg-[var(--gold)] text-[#0F1410]" : "border-white/10 text-[var(--sage)] hover:text-white"}`} data-testid={`admin-tab-${t}`}>
              {t === "all" ? "All" : STATUS_LABELS[t]}
            </button>
          ))}
        </div>
        <label className="relative w-full lg:w-80">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--sage)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, name, suburb" className="field" style={{ paddingLeft: "2.75rem" }} data-testid="admin-search-input" />
        </label>
      </div>
      <OrdersTable orders={shown} onStatus={setStatus} onDelivery={setDelivery} />
      </>}
    </div>
  );
};
